import { createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit'

import { REFERRAL_STATUS_CODE } from '@/lib/constants'
import companyTypeService from '@/lib/services/companyTypeService'
import regulatorService from '@/lib/services/regulatorService'
import {
  selectCurrentCompany,
  selectCurrentGroup,
} from '@/redux/slices/session'
import { RootState } from '@/redux/store'
import { CompanyTypeCode, ReferralStage } from '@/types/misc'
import {
  CommonData,
  CompanyTypeItem,
  ServiceAreaItem,
} from '@/types/responses/common-data'

export const initialState: CommonData = {
  serviceAreas: [],
  fcaServiceAreas: [],
  fcaServiceTypes: [],
  expertiseAreas: [],
  referralStatuses: [],
  regulators: [],
  companyTypes: [],
  networks: [],
  referralSources: [],
}

const sliceName = 'commonData'

export const commonData = createSlice({
  name: sliceName,
  initialState,
  reducers: {
    setCommonData: (_state, { payload }: PayloadAction<CommonData>) => {
      return payload
    },
  },
})

const selectCommonData = (state: RootState) => state.commonData

// If you want to work with only FcaServiceAreaItem[] items, use selectFcaServiceAreas.
// By default, this will return all service areas but you can specify isFca to
// limit to FCA or non FCA service areas.
export const selectServiceAreas = (isFca?: boolean) => {
  return createSelector(selectCommonData, (commonData) => {
    if (typeof isFca === 'undefined') {
      return commonData.serviceAreas
    }

    return commonData.serviceAreas.filter(
      (serviceArea) => serviceArea.isFcaServiceArea === isFca,
    )
  })
}

export const selectSortedServiceAreas = createSelector(
  selectServiceAreas(),
  (serviceAreas) => [...serviceAreas].sort((a, b) => a.sortOrder - b.sortOrder),
)

export const selectCompanyTypes = createSelector(
  selectCommonData,
  (commonData) => commonData.companyTypes,
)

export const selectMainCompanyTypes = createSelector(
  selectCompanyTypes,
  (companyTypes) => companyTypes.filter((companyType) => companyType.main),
)

export const selectCompanyTypeByIRI = (iri: IRI) =>
  createSelector(selectCompanyTypes, (companyTypes) => {
    return companyTypes.find((companyType) => companyType['@id'] === iri)
  })

export const selectExpertiseAreas = createSelector(
  selectCommonData,
  (commonData) => commonData.expertiseAreas,
)

export const selectExpertiseAreasByCompanyTypeIri = (companyTypeIri: IRI) => {
  return createSelector(selectExpertiseAreas, (expertiseAreas) => {
    return expertiseAreas.filter((expertiseArea) => {
      return expertiseArea.companyTypes.some(
        (expertiseAreaCompanyTypeIri) =>
          expertiseAreaCompanyTypeIri === companyTypeIri,
      )
    })
  })
}

export const selectReferralDefaultStatuses = createSelector(
  selectCommonData,
  selectCurrentCompany,
  selectCurrentGroup,
  (commonData, currentCompany, currentGroup) => {
    const shouldIncludePcoStatus = !!(
      currentCompany?.isPco ||
      currentCompany?.hasPcoReferralPartner ||
      currentGroup?.hasCompanyWithPcoReferralPartner
    )

    return shouldIncludePcoStatus
      ? commonData.referralStatuses
      : commonData.referralStatuses.filter(
          (referralStatus) =>
            referralStatus.code !==
            REFERRAL_STATUS_CODE.client_details_passed_to_pco,
        )
  },
)

export const selectReferralStages = createSelector(
  selectCommonData,
  selectCurrentCompany,
  selectCurrentGroup,
  (commonData): ReferralStage[] => {
    return Array.from(
      new Set(
        commonData.referralStatuses.map(
          (referralStatus) => referralStatus.stage as ReferralStage,
        ),
      ),
    ).sort()
  },
)

export const selectFcaServiceAreas = createSelector(
  selectCommonData,
  (commonData) => commonData.fcaServiceAreas,
)

export const selectServiceAreasByCompanyTypeCode = (
  companyTypeCode: CompanyTypeCode,
) => {
  return createSelector(
    selectServiceAreas(),
    selectCompanyTypeByCode(companyTypeCode),
    (serviceAreas, companyType) => {
      return serviceAreas.filter((serviceArea) => {
        return serviceArea.companyTypes.some(
          (companyTypeIri) => companyTypeIri === companyType['@id'],
        )
      })
    },
  )
}

export function getServiceAreasByIds(
  serviceAreas: ServiceAreaItem[],
  ids: number[],
) {
  return ids.length > 0
    ? serviceAreas.filter((serviceArea) => ids.includes(serviceArea.id))
    : []
}

export const selectRegulators = createSelector(
  selectCommonData,
  (commonData) => commonData.regulators,
)

export const selectRegulatorsByCompanyType = (code: CompanyTypeCode) =>
  createSelector(selectRegulators, (regulators) =>
    regulatorService.getRegulatorsByCompanyType(regulators, code),
  )

export const selectCompanyTypeByCode = (code: CompanyTypeCode) =>
  createSelector(selectCompanyTypes, (companyTypes) => {
    return companyTypeService.getCompanyTypeByCodeOrFail(companyTypes, code)
  })

export const selectCompanyTypeById = (id: number) => {
  return createSelector(selectCompanyTypes, (companyTypes): CompanyTypeItem => {
    const companyType = companyTypes.find(
      (companyType) => companyType.id === id,
    )

    if (!companyType) {
      throw new Error(`No company type found with with ID ${id}`)
    }

    return companyType
  })
}

export const selectCompanyTypeNameByCode = (
  code: CompanyTypeCode,
  shortName?: boolean,
) => {
  return createSelector(selectCompanyTypeByCode(code), (companyType) => {
    return shortName ? companyType.shortName : companyType.name
  })
}

export const selectNetworks = createSelector(
  selectCommonData,
  (commonData) => commonData.networks,
)

export const selectReferralSources = createSelector(
  selectCommonData,
  (commonData) => commonData.referralSources,
)

export const selectIsCommonDataLoaded = createSelector(
  selectCommonData,
  (commonData) => commonData.companyTypes.length > 0,
)

export const commonDataReducer = commonData.reducer

export const { setCommonData } = commonData.actions
